import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { LEAD_DETAIL, LEAD_AGENT } from "@/constants/testIds";
import { EnrollSequenceButton } from "@/pages/Sequences";
import { toast, Toaster } from "sonner";
import FileUpload from "@/components/FileUpload";
import {
  Phone,
  Mail,
  MapPin,
  Star,
  Sparkles,
  ExternalLink,
  Send,
  ArrowLeft,
  Bot,
  Mails,
  MessageSquare,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "website_sent", label: "Website Sent" },
  { value: "interested", label: "Interested" },
  { value: "closed_won", label: "Closed Won" },
  { value: "closed_lost", label: "Closed Lost" },
];

function LeadBusinessPhoto({ leadId }) {
  const [files, setFiles] = useState([]);
  const [blobUrl, setBlobUrl] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/files", {
        params: { ref_type: "lead", ref_id: leadId, purpose: "lead_photo", limit: 1 },
      });
      setFiles(data.files || []);
    } catch { /* noop */ }
  }, [leadId]);
  useEffect(() => { if (leadId) load(); }, [leadId, load]);
  useEffect(() => {
    if (!files[0]) return;
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${files[0].id}/download`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data); setBlobUrl(u); revoke = u;
      } catch { /* noop */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [files]);
  return (
    <div className="mb-6" data-testid="lead-business-photo">
      <div className="overline mb-2 flex items-center gap-2"><ImageIcon className="w-3 h-3" /> Business photo</div>
      {blobUrl ? (
        <div className="relative w-full max-w-md">
          <img src={blobUrl} alt="business" className="w-full h-40 object-cover border border-neutral-200" />
          <div className="text-xs text-neutral-500 mt-1 font-mono">Appears on the generated preview website.</div>
        </div>
      ) : (
        <div className="max-w-md">
          <FileUpload purpose="lead_photo" refType="lead" refId={leadId} label="Drop a business photo" onUploaded={load} maxMb={10} />
        </div>
      )}
    </div>
  );
}

export default function LeadDetail() {  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [website, setWebsite] = useState(null);
  const [outreach, setOutreach] = useState([]);
  const [agent, setAgent] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [scriptBusy, setScriptBusy] = useState(false);
  const [script, setScript] = useState("");
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [outreachBusy, setOutreachBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [emailOverride, setEmailOverride] = useState("");
  const [emailOverrideShown, setEmailOverrideShown] = useState(false);
  const [smsSharing, setSmsSharing] = useState(false);
  const [smsOverride, setSmsOverride] = useState("");
  const [smsOverrideShown, setSmsOverrideShown] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    // Best-effort — endpoint requires admin; ignore 403
    api.get("/users").then(({ data }) => setUsers(data.users || [])).catch(() => setUsers([]));
  }, []);

  const changeOwner = async (owner_id) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { owner_id: owner_id || "" });
      setLead(data);
      toast.success(owner_id ? "Owner assigned" : "Owner cleared");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteLead = async () => {
    if (!lead) return;
    if (!window.confirm(`Delete "${lead.name}" permanently? This removes all outreach and generated websites tied to it. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success(`Deleted "${lead.name}".`);
      navigate("/leads");
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data.lead);
      setWebsite(data.website);
      setOutreach(data.outreach);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/websites/generate", { lead_id: id });
      setWebsite(data);
      toast.success("Website generated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenBusy(false);
    }
  };

  const genScript = async () => {
    setScriptBusy(true);
    try {
      const { data } = await api.post("/outreach/call-script", { lead_id: id });
      setScript(data.script);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setScriptBusy(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLead(data);
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendOutreach = async (e) => {
    e.preventDefault();
    setOutreachBusy(true);
    try {
      const { data } = await api.post("/outreach", {
        lead_id: id,
        channel,
        subject: channel === "email" ? subject : "",
        body,
      });
      setSubject("");
      setBody("");
      if (data.status === "sent") {
        toast.success("Email sent!");
      } else if (data.status === "send_failed") {
        toast.error("Send failed — logged instead");
      } else {
        toast.success("Outreach logged");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOutreachBusy(false);
    }
  };

  const callWithAI = async () => {
    if (!window.confirm(`Start an AI voice call to ${lead?.name || "this lead"} at ${lead?.phone}?`)) return;
    try {
      const { data } = await api.post(`/vapi/call/lead/${id}`);
      toast.success(`AI call started — track it in AI Outreach console. Call ID: ${data.vapi_call_id?.slice(0, 8)}…`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sharePreview = async (overrideEmail = null) => {
    setSharing(true);
    try {
      const payload = { lead_id: id };
      if (overrideEmail) payload.to = overrideEmail;
      const { data } = await api.post("/outreach/share-preview", payload);
      toast.success(`Preview emailed → ${overrideEmail || lead?.email}`);
      setEmailOverride("");
      setEmailOverrideShown(false);
      load();
    } catch (err) {
      const msg = formatApiError(err);
      // If no valid email on lead, prompt for one
      if (msg.toLowerCase().includes("no real email")) {
        setEmailOverrideShown(true);
        toast.error("Lead has no real email — enter one below.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSharing(false);
    }
  };

  const sharePreviewSms = async (overridePhone = null) => {
    setSmsSharing(true);
    try {
      const payload = { lead_id: id };
      if (overridePhone) payload.to = overridePhone;
      const { data } = await api.post("/outreach/share-preview-sms", payload);
      toast.success(`Preview texted → ${overridePhone || lead?.phone}`);
      setSmsOverride("");
      setSmsOverrideShown(false);
      load();
    } catch (err) {
      const msg = formatApiError(err);
      if (msg.toLowerCase().includes("valid phone") || msg.toLowerCase().includes("no valid phone")) {
        setSmsOverrideShown(true);
        toast.error("Lead phone is not deliverable — enter one below.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSmsSharing(false);
    }
  };

  if (!lead) return <div className="p-10 text-neutral-500">Loading lead…</div>;

  const previewUrl = website ? `${API}/preview/${website.id}` : null;

  return (
    <div data-testid={LEAD_DETAIL.container} className="p-10">
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between mb-6">
        <Link to="/leads" className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-black">
          <ArrowLeft className="w-4 h-4" /> Back to pipeline
        </Link>
        <button
          onClick={deleteLead}
          disabled={deleting}
          className="inline-flex items-center gap-2 text-sm border border-rose-300 text-rose-700 hover:bg-rose-50 px-3 py-1.5 disabled:opacity-40"
          data-testid="lead-delete-btn"
        >
          <Trash2 className="w-4 h-4" /> {deleting ? "Deleting…" : "Delete lead"}
        </button>
      </div>

      <LeadBusinessPhoto leadId={lead.id} />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
        <div>
          <div className="overline mb-2">
            {lead.industry} · {lead.city}, {lead.state}
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">
            {lead.name}
          </h1>
          <div className="mt-3 flex items-center gap-6 text-sm text-neutral-600">
            <span className="flex items-center gap-2 font-mono">
              <Phone className="w-4 h-4" /> {lead.phone}
            </span>
            <span className="flex items-center gap-2 font-mono">
              <Mail className="w-4 h-4" /> {lead.email}
            </span>
            <span className="flex items-center gap-2">
              <Star className="w-4 h-4" style={{ color: "#FFD700" }} />{" "}
              <span className="font-semibold">{lead.rating}</span> ({lead.review_count})
            </span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-sm text-neutral-500">
            <MapPin className="w-4 h-4" />
            {lead.address}
          </div>
        </div>
        <div className="flex flex-col gap-2 min-w-[220px]">
          <label className="overline">Status</label>
          <select
            data-testid={LEAD_DETAIL.statusSelect}
            value={lead.status}
            onChange={(e) => changeStatus(e.target.value)}
            className="px-3 py-2 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {users.length > 0 && (
            <>
              <label className="overline mt-1">Owner</label>
              <select
                data-testid="lead-owner-select"
                value={lead.owner_id || ""}
                onChange={(e) => changeOwner(e.target.value)}
                className="px-3 py-2 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
              >
                <option value="">— Unassigned —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}{u.role === "admin" ? " (admin)" : ""}
                  </option>
                ))}
              </select>
            </>
          )}
          <EnrollSequenceButton targetId={lead.id} targetType="lead" onEnrolled={load} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* AI AGENT (spans full width) */}
        <section className="lg:col-span-3 bg-white border border-neutral-200 p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-black text-white flex items-center justify-center flex-shrink-0">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <div className="overline mb-1">AI Agent</div>
              {agent ? (
                <>
                  <div className="font-display text-xl font-bold">
                    {agent.name}
                  </div>
                  <div className="text-sm text-neutral-500">
                    {agent.role} · {agent.booking_enabled ? "Booking enabled" : "Chat only"}
                    {website ? " · Auto-embedded on preview site" : ""}
                  </div>
                </>
              ) : (
                <>
                  <div className="font-display text-xl font-bold">
                    No agent yet
                  </div>
                  <div className="text-sm text-neutral-500">
                    Create a chat + booking assistant that auto-embeds on this
                    lead&apos;s preview website.
                  </div>
                </>
              )}
            </div>
          </div>
          <div>
            {agent ? (
              <Link
                to={`/agents/${agent.id}`}
                data-testid={LEAD_AGENT.editLink}
                className="lf-btn-outline text-sm inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Edit & test
              </Link>
            ) : (
              <Link
                to={`/agents/new?lead_id=${id}`}
                data-testid={LEAD_AGENT.createBtn}
                className="lf-btn-primary text-sm inline-flex items-center gap-2"
              >
                <Bot className="w-4 h-4" /> Create AI agent
              </Link>
            )}
          </div>
        </section>

        {/* WEBSITE PREVIEW */}
        <section className="lg:col-span-2 bg-white border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline mb-1">Free preview website</div>
              <h2 className="font-display text-2xl font-bold">
                AI-generated site
              </h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              {previewUrl && (
                <>
                  <a
                    data-testid={LEAD_DETAIL.previewLink}
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="lf-btn-outline flex items-center gap-2 text-xs"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Open
                  </a>
                  <button
                    data-testid="lead-share-preview-btn"
                    onClick={() => sharePreview()}
                    disabled={sharing}
                    className="lf-btn-outline flex items-center gap-2 text-xs"
                    title="Email the preview link to the business"
                  >
                    <Mails className="w-3.5 h-3.5" />
                    {sharing ? "Sending…" : "Email to lead"}
                  </button>
                  <button
                    data-testid="lead-share-preview-sms-btn"
                    onClick={() => sharePreviewSms()}
                    disabled={smsSharing}
                    className="lf-btn-outline flex items-center gap-2 text-xs"
                    title="Text the preview link to the business"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    {smsSharing ? "Sending…" : "Text to lead"}
                  </button>
                </>
              )}
              <button
                data-testid={LEAD_DETAIL.generateBtn}
                onClick={generate}
                disabled={genBusy}
                className="lf-btn-primary flex items-center gap-2 text-sm"
              >
                <Sparkles className="w-4 h-4" />
                {genBusy ? "Generating…" : website ? "Regenerate" : "Generate website"}
              </button>
            </div>
          </div>

          {emailOverrideShown && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 flex items-center gap-2">
              <input
                data-testid="lead-share-preview-override-input"
                type="email"
                placeholder="owner@example.com"
                value={emailOverride}
                onChange={(e) => setEmailOverride(e.target.value)}
                className="flex-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black"
              />
              <button
                data-testid="lead-share-preview-override-send"
                onClick={() => emailOverride.includes("@") && sharePreview(emailOverride)}
                disabled={sharing || !emailOverride.includes("@")}
                className="lf-btn-primary text-xs px-3 py-2"
              >
                Send email
              </button>
              <button
                onClick={() => setEmailOverrideShown(false)}
                className="text-neutral-500 text-xs px-2"
              >
                Cancel
              </button>
            </div>
          )}

          {smsOverrideShown && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 flex items-center gap-2">
              <input
                data-testid="lead-share-preview-sms-override-input"
                type="tel"
                placeholder="+15125551234"
                value={smsOverride}
                onChange={(e) => setSmsOverride(e.target.value)}
                className="flex-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black font-mono"
              />
              <button
                data-testid="lead-share-preview-sms-override-send"
                onClick={() => smsOverride.length >= 10 && sharePreviewSms(smsOverride)}
                disabled={smsSharing || smsOverride.length < 10}
                className="lf-btn-primary text-xs px-3 py-2"
              >
                Send SMS
              </button>
              <button
                onClick={() => setSmsOverrideShown(false)}
                className="text-neutral-500 text-xs px-2"
              >
                Cancel
              </button>
            </div>
          )}

          {website ? (
            <iframe
              data-testid={LEAD_DETAIL.previewIframe}
              src={previewUrl}
              title="preview"
              className="lf-preview-frame"
            />
          ) : (
            <div className="border border-dashed border-neutral-300 p-16 text-center">
              <div className="overline mb-3">No preview yet</div>
              <p className="text-neutral-500 max-w-md mx-auto">
                Click &ldquo;Generate website&rdquo; to create a live preview using
                industry template + Claude Sonnet 4.5 copy.
              </p>
            </div>
          )}
        </section>

        {/* OUTREACH */}
        <aside className="bg-white border border-neutral-200 p-6">
          <div className="overline mb-2">Outreach</div>
          <h2 className="font-display text-2xl font-bold mb-4">
            Reach out & track
          </h2>

          {/* AI Voice Call — one-click */}
          <button
            type="button"
            onClick={callWithAI}
            disabled={!lead?.phone}
            data-testid="lead-call-with-ai-btn"
            className="w-full mb-5 px-4 py-3 bg-[#0F2340] text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#E24E1B] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            📞 Call with AI (Vapi)
            {!lead?.phone && <span className="text-xs opacity-80">· no phone on lead</span>}
          </button>

          <form onSubmit={sendOutreach} className="space-y-4">
            <div>
              <label className="overline block mb-2">Channel</label>
              <select
                data-testid={LEAD_DETAIL.outreachChannelSelect}
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="call">Call log</option>
              </select>
            </div>
            {channel === "email" && (
              <div>
                <label className="overline block mb-2">Subject</label>
                <input
                  data-testid={LEAD_DETAIL.outreachSubject}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
                  placeholder="Free website preview for your business"
                />
              </div>
            )}
            <div>
              <label className="overline block mb-2">Message</label>
              <textarea
                data-testid={LEAD_DETAIL.outreachBody}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                required
                className="w-full px-3 py-2 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black font-mono"
                placeholder={
                  channel === "call"
                    ? "Notes from the call…"
                    : "Hi [Owner], I noticed your business doesn't have a website yet. I built you a free preview…"
                }
              />
            </div>
            <button
              data-testid={LEAD_DETAIL.outreachSubmit}
              disabled={outreachBusy}
              type="submit"
              className="lf-btn-primary w-full flex items-center justify-center gap-2 text-sm"
            >
              <Send className="w-4 h-4" />
              {outreachBusy ? "Logging…" : "Log outreach"}
            </button>
            <p className="text-xs text-neutral-500 font-mono">
              {channel === "email"
                ? "Real email sent via Resend when lead has a valid email."
                : channel === "sms"
                ? "Actual send requires Twilio keys — currently logged only."
                : "Call notes are logged only."}
            </p>
          </form>

          <div className="mt-8 pt-6 border-t border-neutral-200">
            <div className="overline mb-4">
              History ({outreach.length})
            </div>
            <div data-testid={LEAD_DETAIL.outreachList} className="space-y-3">
              {outreach.map((o) => (
                <div key={o.id} className="border border-neutral-200 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase tracking-widest">
                      {o.channel}
                    </span>
                    <div className="flex items-center gap-2">
                      {o.status === "sent" && (
                        <span className="pill pill-interested text-[9px]">Sent · {o.provider || "email"}</span>
                      )}
                      {o.status === "send_failed" && (
                        <span className="pill pill-closed_lost text-[9px]">Send failed</span>
                      )}
                      {o.status === "logged" && (
                        <span className="pill pill-new text-[9px]">Logged</span>
                      )}
                      <span className="text-xs text-neutral-500 font-mono">
                        {new Date(o.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {o.subject && (
                    <div className="text-sm font-semibold mb-1">
                      {o.subject}
                    </div>
                  )}
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap">
                    {o.body.length > 200 ? o.body.slice(0, 200) + "…" : o.body}
                  </div>
                </div>
              ))}
              {outreach.length === 0 && (
                <div className="text-xs text-neutral-400 italic">
                  No outreach yet.
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* CALL SCRIPT */}
      <section className="mt-6 bg-white border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline mb-1">Cold-call script</div>
            <h2 className="font-display text-2xl font-bold">
              AI-generated pitch
            </h2>
          </div>
          <button
            data-testid={LEAD_DETAIL.scriptBtn}
            onClick={genScript}
            disabled={scriptBusy}
            className="lf-btn-outline flex items-center gap-2 text-sm"
          >
            <Sparkles className="w-4 h-4" />
            {scriptBusy ? "Writing…" : script ? "Rewrite" : "Generate script"}
          </button>
        </div>
        {script ? (
          <pre
            data-testid={LEAD_DETAIL.scriptBody}
            className="whitespace-pre-wrap font-mono text-sm text-neutral-800 leading-relaxed bg-neutral-50 border border-neutral-200 p-6"
          >
            {script}
          </pre>
        ) : (
          <div className="border border-dashed border-neutral-300 p-10 text-center text-neutral-500 text-sm">
            Click &ldquo;Generate script&rdquo; for a 60-90s cold-call outline tailored to
            this lead.
          </div>
        )}
      </section>
    </div>
  );
}
